import React, { useEffect, useRef } from "react";
import Avatar from "components/BaseWidgets/Avatar";
import profilePic from "assets/images/png/dummy_avatar.png";
import "./ChatMessages.scss";
import ProfilePreview from "./ProfilePreview";
import { getUser } from "utils/storage";
import { CloudDownloadOutlined, FilePdfOutlined } from "@ant-design/icons";

const ChatMessages = ({
  messages,
  currentUserId,
  selectedChatId,
  participants,
  userChats,
  chatData,
  selectedThread,
}) => {
  const containerRef = useRef(null);
  const userImage = getUser()?.image;
  const selectedChat = userChats.find(
    (chat) => chat.id === selectedChatId || chat.chatId === selectedChatId
  );

  // console.log(messages, "messages");

  // Keep only valid messages (fix missing sender issues)
  const sanitizedMessages = messages.filter(
    (msg) => msg.senderId && msg.timestamp?.seconds
  );

  // Auto-scroll to bottom
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [sanitizedMessages, selectedChatId]);

  if (!sanitizedMessages.length || !selectedChatId) return null;

  // Group by date
  const groupMessagesByDate = (msgs) => {
    const groups = { Today: [], Yesterday: [], Previous: [] };

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    msgs.forEach((msg) => {
      const msgDate = new Date(msg.timestamp.seconds * 1000);

      if (msgDate.toDateString() === today.toDateString()) {
        groups.Today.push(msg);
      } else if (msgDate.toDateString() === yesterday.toDateString()) {
        groups.Yesterday.push(msg);
      } else {
        groups.Previous.push(msg);
      }
    });

    return groups;
  };

  const grouped = groupMessagesByDate(sanitizedMessages);

  const renderGroup = (label, msgs) => {
    if (!msgs.length) return null;

    // console.log(selectedChat, "selectedChat");

    return (
      <div key={label}>
        <span className="message-time-tag">{label}</span>

        {msgs.map((msg) => {
          const isSender = String(msg.senderId) === String(currentUserId);
          // console.log(isSender, "j");

          const userAvatar = isSender
            ? userImage || profilePic
            : selectedThread?.profile_image || profilePic;

          return (
            <div
              key={msg.id || msg.messageId}
              className={`message ${isSender ? "right" : "left"}`}
            >
              {/* Show avatar only for incoming */}
              {!isSender && <Avatar url={userAvatar} className="avatar " />}

              <div className="content">
                {/* Text */}
                {msg.text && <div className="bubble">{msg.text}</div>}

                {/* Media */}
                {msg.mediaUrl && msg.type === "image" && (
                  <img
                    src={msg.mediaUrl}
                    alt="sent media"
                    className="chat-image"
                    loading="lazy"
                  />
                )}

                {msg.type === "file" && (
                  <div
                    className="flex gap-2 items-center file-container"
                    style={{
                      minHeight: "60px",
                      padding: "8px",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      cursor: "pointer",
                    }}
                    onClick={() => window.open(msg.mediaUrl, "_blank")}
                  >
                    <FilePdfOutlined
                      style={{ fontSize: "20px", flexShrink: 0 }}
                    />
                    <p
                      style={{
                        fontSize: "13px",
                        margin: 0,
                        flex: 1,
                        wordBreak: "break-word",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {msg.mediaUrl.split("/").pop()}
                    </p>
                    <CloudDownloadOutlined
                      style={{ fontSize: "20px", flexShrink: 0 }}
                    />
                  </div>
                )}

                {/* Timestamp */}
                <div className="time">
                  {new Date(msg.timestamp.seconds * 1000).toLocaleTimeString(
                    [],
                    { hour: "2-digit", minute: "2-digit" }
                  )}
                </div>
              </div>

              {/* Avatar on outgoing right side */}
              {isSender && <Avatar url={userAvatar} className="avatar" />}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="chat-messages" ref={containerRef}>
      <ProfilePreview
        participants={participants}
        data={chatData}
        userChats={userChats}
        selectedChatId={selectedChatId}
        selectedThread={selectedThread}
      />
      {renderGroup("Previous", grouped.Previous)}
      {renderGroup("Yesterday", grouped.Yesterday)}
      {renderGroup("Today", grouped.Today)}
    </div>
  );
};

export default ChatMessages;
